import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import io from "socket.io-client";
import { SOCKET_URL, DOMAIN_URL } from "../../config";
import { useSocket } from "../../components/SocketConnection";
import { getUserData } from "../../Actions/common";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [dropdownNotifications, setDropdownNotifications] = useState([]);
    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [userId, setUserId] = useState(null);
    const [anchorElement, setAnchorElement] = useState(null);
    const isMounted = useRef(false);
    const chunkSize = 5;
    const { socketConnection, setSocketConnectionData } = useSocket();

    const unseenNotification = notifications?.filter(
        (notification) => !notification?.isRead
    ).length;

    useEffect(() => {
        if (!socketConnection && !isMounted.current) {
            isMounted.current = true;

            const newConnection = io(SOCKET_URL, {
                reconnection: true,
                reconnectionDelay: 1000,
            });
            newConnection.on("connect", () => {
                const userData = localStorage.getItem("userData")
                    ? getUserData()
                    : null;
                newConnection.emit("join", userData?.id, DOMAIN_URL);
            });
            setSocketConnectionData(newConnection);
        }

        return () => {
            if (socketConnection) {
                socketConnection.disconnect();
                setSocketConnectionData(null);
            }
        };
    }, [socketConnection, setSocketConnectionData]);

    const sortNotifications = (list = []) =>
        [...list].sort((a, b) => {
            if (a.isRead === b.isRead) {
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            }
            return a.isRead ? 1 : -1;
        });

    const addNotifications = (newNotifications, nextUserId) => {
        setUserId(nextUserId);

        setNotifications((prevNotifications) => {
            const incoming = newNotifications.filter(
                (n) => Number(n.toUser) === nextUserId
            );
            const existingMap = new Map(prevNotifications.map((n) => [n.id, n]));
            incoming.forEach((n) => existingMap.set(n.id, n));
            return sortNotifications(Array.from(existingMap.values()));
        });

        setDropdownNotifications((prev) => {
            const incoming = newNotifications.filter(
                (n) => Number(n.toUser) === nextUserId && !n.isRead
            );
            const existingMap = new Map(
                prev.filter((n) => !n.isRead).map((n) => [n.id, n])
            );
            incoming.forEach((n) => existingMap.set(n.id, n));
            return sortNotifications(Array.from(existingMap.values()));
        });

        setDisplayedNotifications((prev) => {
            const incoming = newNotifications.filter(
                (n) => Number(n.toUser) === nextUserId && !n.isRead
            );
            const existingMap = new Map(
                prev.filter((n) => !n.isRead).map((n) => [n.id, n])
            );
            incoming.forEach((n) => existingMap.set(n.id, n));
            return sortNotifications(Array.from(existingMap.values())).slice(
                0,
                Math.max(chunkSize, prev.length || chunkSize)
            );
        });
    };

    const resolveUserId = () => {
        if (userId) return userId;
        const userData = localStorage.getItem("userData")
            ? getUserData()
            : null;
        return userData?.id || null;
    };

    const markIdsAsRead = (ids = []) => {
        if (!ids.length) return;

        const activeUserId = resolveUserId();
        if (socketConnection && activeUserId) {
            if (!userId) setUserId(activeUserId);
            socketConnection.emit("markNotificationsRead", {
                userId: activeUserId,
                notificationIds: ids,
            });
        }

        setNotifications((prev) =>
            prev.map((notif) =>
                ids.includes(notif.id) ? { ...notif, isRead: true } : notif
            )
        );

        setDropdownNotifications((prev) =>
            prev.filter((notif) => !ids.includes(notif.id))
        );

        setDisplayedNotifications((prev) =>
            prev.filter((notif) => !ids.includes(notif.id))
        );
    };

    const handleNotificationClick = (event) => {
        const activeUserId = resolveUserId();
        if (!activeUserId) {
            console.error("User ID is undefined");
            return;
        }
        if (!userId) setUserId(activeUserId);

        setAnchorElement(event.currentTarget);
    };

    const loadMoreNotifications = () => {
        const nextChunk = dropdownNotifications.slice(
            displayedNotifications.length,
            displayedNotifications.length + chunkSize
        );

        if (nextChunk.length > 0) {
            setDisplayedNotifications((prev) => [...prev, ...nextChunk]);
        }
    };

    const markAllNotificationsRead = () => {
        const unreadIds = notifications
            .filter((notification) => !notification.isRead)
            .map((notification) => notification.id);

        if (unreadIds.length) {
            markIdsAsRead(unreadIds);
        }

        setDropdownNotifications([]);
        setDisplayedNotifications([]);
    };

    const handleCloseNotification = () => {
        setDisplayedNotifications((prev) => {
            if (!dropdownNotifications.length) return [];
            return dropdownNotifications.slice(
                0,
                Math.min(chunkSize, dropdownNotifications.length || prev.length)
            );
        });
        setAnchorElement(null);
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                dropdownNotifications,
                loadMoreNotifications,
                addNotifications,
                handleNotificationClick,
                anchorElement,
                handleCloseNotification,
                displayedNotifications,
                unseenNotification,
                markAllNotificationsRead,
                setNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => useContext(NotificationContext);
